import { Injectable, OnDestroy, computed, signal } from '@angular/core';
import { ApiService } from './api.service';
import { ScenarioRun } from './models';
import { WorkspaceService } from './workspace.service';

const ACTIVE_POLL_MS = 3_000;
const IDLE_POLL_MS = 30_000;

@Injectable({ providedIn: 'root' })
export class NotificationService implements OnDestroy {
  private readonly runningCount = signal(0);
  private readonly unviewedRuns = signal<ScenarioRun[]>([]);
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private currentInterval = 0;
  private alive = true;
  private previousRunningCount = 0;

  readonly running = computed(() => this.runningCount());
  readonly unviewedCompleted = computed(() => this.unviewedRuns());
  readonly unviewedCount = computed(() => this.unviewedRuns().length);
  readonly totalBadge = computed(() => this.runningCount() + this.unviewedRuns().length);

  constructor(
    private readonly api: ApiService,
    private readonly workspace: WorkspaceService,
  ) {}

  ngOnDestroy(): void {
    this.alive = false;
    this.stopTimer();
  }

  start(): void {
    if (this.pollTimer) return;
    void this.refresh();
    this.schedulePoll(ACTIVE_POLL_MS);
  }

  /** Call when a scenario is launched to ensure active polling. */
  kick(): void {
    void this.refresh();
    this.schedulePoll(ACTIVE_POLL_MS);
  }

  async markRunViewed(runId: string): Promise<void> {
    try {
      await this.api.markRunViewed(runId);
    } catch { /* silent */ }
    this.unviewedRuns.update((runs) => runs.filter((r) => r.id !== runId));
  }

  private schedulePoll(interval: number): void {
    if (this.currentInterval === interval && this.pollTimer) return;
    this.stopTimer();
    this.currentInterval = interval;
    this.pollTimer = setInterval(() => void this.refresh(), interval);
  }

  private stopTimer(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
      this.currentInterval = 0;
    }
  }

  private async refresh(): Promise<void> {
    if (!this.alive) return;
    try {
      const [activeRuns, runs] = await Promise.all([
        this.api.listActiveRuns(),
        this.api.listRuns(),
      ]);

      // Count running scenarios across ALL accessible brands (not just the active
      // brand), unioned with optimistic local launches not yet reflected server-side.
      const activeScenarioIds = new Set(activeRuns.map((r) => r.scenarioId));
      for (const scenarioId of this.api.runningScenarioIds) {
        activeScenarioIds.add(scenarioId);
      }
      const running = activeScenarioIds.size;
      this.runningCount.set(running);

      const unviewed = runs.filter(
        (r) => r.status === 'completed' && !r.viewedAt,
      );
      this.unviewedRuns.set(unviewed);

      if (this.previousRunningCount > running) {
        void this.workspace.refreshCredits();
      }
      this.previousRunningCount = running;

      const needsActive = running > 0 || this.api.runningScenarioIds.size > 0;
      this.schedulePoll(needsActive ? ACTIVE_POLL_MS : IDLE_POLL_MS);
    } catch { /* silent */ }
  }
}
